import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, CircularProgress, Typography, debounce } from "@mui/material";
import { DataGrid, GridColDef, GridFilterModel, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";
import { axiosWithAuth } from "../plugins/axios";
import { useNavigate } from "react-router-dom";

const ListOfQuizzes = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [overallAccuracy, setOverallAccuracy] = useState<string | null>(null);
  const [avgDeviation, setAvgDeviation] = useState<number | null>(null);
  const [maxDeviation, setMaxDeviation] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();

  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_PIPELINE_API;

  useEffect(() => {
    const fetchQuizzes = async () => {
      setError(null);
      try {
        const response = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/listOfQuizzes`,
          {
            params: {
               page: page + 1,
              limit: rowsPerPage,
              sortColumn: sortModel[0]?.field || null,
              sortDirection: sortModel[0]?.sort || null,
              filterColumn: filterModel?.items[0]?.field || null,
              filterValue: filterModel?.items[0]?.value || null,
              filterOperator: filterModel?.items[0]?.operator || null,},
          },
          "get"
        );
        setQuizzes(response.data?.groupedQuizzes);
        setTotalCount(response.data?.totalCount || 0);
      } catch (err) {
        setError("Failed to load quizzes");
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, [apiUrl, page, rowsPerPage,sortModel, filterModel]);
  
  const debounceFetchQuizzes = useMemo(
    () =>
      debounce((filter) => {
        setFilterModel(filter);
      }, 1000),
    [filterModel]
  );

  useEffect(() => {
    const fetchOverallMetrics = async () => {
      setMetricsError(null);
      try {
        const response = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/overallMetrics`,
          {},
          "get"
        );
        setOverallAccuracy(response.data?.overallMetrics?.overallAccuracy || "N/A");
        setAvgDeviation(Math.floor(response.data?.overallMetrics?.averageDeviation) || 0);
        setMaxDeviation(Math.floor(response.data?.overallMetrics?.maxDeviation) || 0);
      } catch (err) {
        setMetricsError("Failed to load overall metrics");
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchOverallMetrics();
  }, [apiUrl]);
  const columns: GridColDef[] = [
    {
      field: "quizId",
      headerName: "ID",
      width: 80, 
      renderCell: (params: GridRenderCellParams) => (
        <Button
          color="primary"
          onClick={() =>
            navigate(`/performanceDashboard/listOfQuizzes/cohort/${params?.row?.courseId}/quiz/${params.value}/evaluations`)
          }
        >
          {params.value}
        </Button>
      ),
    },
    { field: "quizName", headerName: "Quiz Name", width: 250 },
    { field: "courseId", headerName: "Cohort ID", width: 100 },
    { field: "submissionEvaluated", headerName: "Submissions Evaluated", width: 180 },
    { field: "belowMinus10", headerName: "Deviation <-10%", width: 150 },
    { field: "minus10toMinus5", headerName: "Deviation -10% to -5%", width: 160 },
    { field: "minus5to0", headerName: "Deviation -5% to 0%", width: 160 },
    { field: "exact0", headerName: "Deviation 0%", width: 130 },
    { field: "zeroTo5", headerName: "Deviation 0% to 5%", width: 160 },
    { field: "fiveTo10", headerName: "Deviation 5% to 10%", width: 160 },
    { field: "above10", headerName: "Deviation >10%", width: 160 },
    { field: "ExactAccuracy", headerName: "Exact Accuracy", width: 150 },
    { field: "AccuracyWithin5", headerName: "Accuracy within 5%", width: 170 },
    { field: "AccuracyWithin10", headerName: "Accuracy within 10%", width: 170 },
  ];

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom mb={4}>
        List of Quizzes
      </Typography>

      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={4} width="100%">
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Typography fontWeight="bold">Overall Accuracy:</Typography>
            <Typography>{overallAccuracy}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Typography fontWeight="bold">Avg. Deviation:</Typography>
            <Typography>{avgDeviation}%</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Typography fontWeight="bold">Highest Deviation:</Typography>
            <Typography>{maxDeviation}%</Typography>
          </Box>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={1} mb={5}>
        <Typography fontWeight="bold">Overall Accuracy (With ±10%):</Typography>
        <Typography>{overallAccuracy}</Typography>
      </Box> */}

      <DataGrid
        rows={quizzes}
        columns={columns}
        getRowId={(row) => row.quizId}
        rowHeight={70}
        paginationMode="server"
        sortingMode="server"
        pagination
        paginationModel={{
          page: page,
          pageSize: rowsPerPage,
        }}
        rowCount={totalCount}
        onPaginationModelChange={(newPaginationModel) => {
          setPage(newPaginationModel.page);
          setRowsPerPage(newPaginationModel.pageSize);
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        filterMode="server"
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        onFilterModelChange={(filter) => debounceFetchQuizzes(filter)}
      />
    </Box>
  );
};

export default ListOfQuizzes;